import { useEffect, useRef, useState } from 'react';

import { narrowScriptCapabilities } from '../editor/editor-panels';
import { formatDiagnostic, formatScriptValue } from '../editor/scriptConsoleFormat';
import { HollowScriptEditor } from '../editor/scripting/monaco/HollowScriptEditor';
import { TIER2_SCRIPTING_ENABLED } from '../config/tier2ScriptingFeature';
import { usePlayScriptConsoleStore } from './playScriptConsoleStore';

/**
 * The published play surface's tier-2 script console - the tier-2 mirror of
 * `PreviewRoute`'s own `PreviewConsoleOverlay`/`DockConsole` pair
 * (`screens-editor.tsx`/`editor-panels.tsx`), but self-contained rather than
 * reusing `DockConsole` directly: that component also serves Edit mode's
 * docked Console tab (its `stats` command reaches `useMapEditorStore`,
 * editor-only state with no equivalent on the public play surface), and
 * pulling that whole component onto `/play/<slug>/live` would couple this
 * route to editor-only state for no reason. Reuses `narrowScriptCapabilities`
 * from `editor-panels.tsx` (task item 4's own instruction: reuse the
 * existing narrowing function rather than re-deriving it) - everything else
 * here is deliberately smaller than `DockConsole`.
 *
 * Renders nothing at all unless `TIER2_SCRIPTING_ENABLED` is on - see that
 * flag's own doc comment for why (`docs/editor-consolidation/
 * play-origin-isolation.md`'s unshipped origin split). This is the only
 * piece of tier-2 UI a real player could ever encounter, so it is the one
 * place this pass gates hardest: disabled means this component is not just
 * inert, it does not mount at all.
 */
export function PlayScriptConsole() {
  if (!TIER2_SCRIPTING_ENABLED) return null;
  return <PlayScriptConsoleInner />;
}

type ConsoleLineKind = 'sys' | 'cmd' | 'ok' | 'err' | 'out';
interface ConsoleLine {
  k: ConsoleLineKind;
  t: string;
}

const CONSOLE_BANNER: ConsoleLine = { k: 'sys', t: 'HollowScript console (tier 2 · QuickJS-sandboxed) · type help' };

function PlayScriptConsoleInner() {
  const open = usePlayScriptConsoleStore((state) => state.open);
  const runtime = usePlayScriptConsoleStore((state) => state.runtime);
  const allowedCapabilities = usePlayScriptConsoleStore((state) => state.allowedCapabilities);
  const [lines, setLines] = useState<ConsoleLine[]>([CONSOLE_BANNER]);
  const [val, setVal] = useState('');
  const [running, setRunning] = useState(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const el = endRef.current;
    if (el && el.parentElement) el.parentElement.scrollTop = el.parentElement.scrollHeight;
  }, [lines]);

  if (!open) return null;

  async function run() {
    const source = val.trim();
    if (!source || running) return;
    const cmdLine: ConsoleLine = { k: 'cmd', t: source };
    if (source === 'help') {
      setLines((l) => [...l, cmdLine, { k: 'sys', t: 'clear · or any HollowScript program (uses print, entities, variables, audio, logic, input, clock, random)' }]);
      setVal('');
      return;
    }
    if (source === 'clear') {
      setLines([CONSOLE_BANNER]);
      setVal('');
      return;
    }
    if (!runtime) {
      setLines((l) => [...l, cmdLine, { k: 'err', t: 'No live play session - the game has not finished loading yet.' }]);
      setVal('');
      return;
    }
    setLines((l) => [...l, cmdLine]);
    setVal('');
    setRunning(true);
    try {
      const outcome = await runtime.runScript(source, {
        capabilities: narrowScriptCapabilities(allowedCapabilities),
        onPrint: (text) => setLines((l) => [...l, { k: 'out', t: text }]),
      });
      if (outcome.ok) {
        setLines((l) => [...l, { k: 'ok', t: '✓ ' + formatScriptValue(outcome.result) }]);
      } else {
        setLines((l) => [...l, ...outcome.diagnostics.map((d): ConsoleLine => ({ k: 'err', t: formatDiagnostic(d) }))]);
      }
    } finally {
      setRunning(false);
    }
  }

  const colour: Record<ConsoleLineKind, string> = { sys: 'var(--ss-text-3)', cmd: 'var(--ss-text)', ok: 'var(--ss-success)', err: 'var(--ss-danger)', out: 'var(--ss-text-2)' };
  return (
    <div style={{
      position: 'absolute', bottom: 14, left: 14, right: 14, maxWidth: 640, height: 220,
      background: 'rgba(10,10,12,0.92)', backdropFilter: 'blur(10px)',
      border: '1px solid var(--ss-border-soft)', borderRadius: 12, overflow: 'hidden',
      display: 'flex', flexDirection: 'column', boxShadow: '0 12px 40px rgba(0,0,0,0.5)', zIndex: 60,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 14px', borderBottom: '1px solid var(--ss-border-soft)', flexShrink: 0 }}>
        <span style={{ fontFamily: 'var(--ss-sans)', fontWeight: 500, fontSize: 9.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--ss-accent)' }}>Console (tier 2)</span>
        <button onClick={() => usePlayScriptConsoleStore.getState().toggleOpen()} title="Close (`)" style={{ border: 'none', background: 'transparent', color: 'var(--ss-text-3)', fontSize: 10, padding: '4px 8px' }}>✕</button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 16px', fontFamily: 'var(--ss-mono)', fontSize: 11.5, lineHeight: 1.8 }}>
          {lines.map((l, i) => (
            <div key={i} style={{ color: colour[l.k], whiteSpace: 'pre-wrap' }}>
              {l.k === 'cmd' ? <span style={{ color: 'var(--ss-accent)' }}>» </span> : null}{l.t}
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, padding: '8px 16px', borderTop: '1px solid var(--ss-border-soft)' }}>
          <span style={{ fontFamily: 'var(--ss-mono)', fontSize: 12, color: 'var(--ss-accent)', fontWeight: 600, paddingBottom: 3 }}>»</span>
          <div style={{ flex: 1 }}>
            <HollowScriptEditor
              value={val}
              onChange={setVal}
              onRunRequested={() => void run()}
              capabilities={narrowScriptCapabilities(allowedCapabilities)}
              theme="dark"
              readOnly={running}
              height={64}
              placeholder={runtime ? 'HollowScript source (Enter to run, Shift+Enter for a new line)' : 'HollowScript command (waiting for the game to load)'}
            />
          </div>
          <span className="ss-kbd">{running ? '…' : '↵'}</span>
        </div>
      </div>
    </div>
  );
}
